import { TFunction } from "i18next";
import {
  BillingFrequency,
  ChargeType,
  Pricing,
  PricingModel,
  ServiceDuration,
  ServiceItem
} from "@/pages/settings/pages/products-v2/types";
import type { SingleSelectOption } from "@/components/controls/SingleSelect";
import {
  BILLING_FREQUENCY_OPTIONS,
  SERVICE_DURATION_OPTIONS
} from "@/pages/settings/pages/products-v2/consts";
import {
  formatCurrency,
  getBillingFrequencyLabel,
  getBillingPeriodLabel,
  getServiceDurationInMonths,
  getServiceDurationLabel,
  isValidQuantity
} from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { DealServiceLine, FLEXIBLE_TERM_ID } from "./types";

export const isFlexibleService = (service: ServiceItem): boolean =>
  service.pricingModel === PricingModel.FLEXIBLE;

export const isFlatRateService = (service: ServiceItem): boolean =>
  service.pricingModel === PricingModel.FLAT_RATE;

export const isOneTimeService = (service: ServiceItem): boolean =>
  service.chargeType === ChargeType.ONE_TIME;

// A custom/flexible line can be billed once even if its service isn't
// configured as one-time, by picking the "Once" billing frequency.
export const isEffectivelyOneTime = (line: DealServiceLine): boolean =>
  isOneTimeService(line.service) ||
  line.billingFrequency === BillingFrequency.ONCE;

export const getSelectedPricing = (line: DealServiceLine): Pricing | null =>
  line.service.pricing.find((pricing) => pricing.id === line.pricingId) ?? null;

// A custom tier defers duration, frequency and price, so the row asks for them.
export const isCustomTerm = (line: DealServiceLine): boolean =>
  getSelectedPricing(line)?.serviceItemPeriods === ServiceDuration.CUSTOM;

export const hasSelectedTerm = (line: DealServiceLine): boolean =>
  isFlexibleService(line.service)
    ? line.pricingId === FLEXIBLE_TERM_ID
    : !!getSelectedPricing(line);

export interface LineFieldVisibility {
  quantity: boolean;
  duration: boolean;
  billingFrequency: boolean;
  price: boolean;
}

export const getLineFieldVisibility = (
  line: DealServiceLine
): LineFieldVisibility => {
  const isFlexible = isFlexibleService(line.service);
  const needsCustomInputs = isFlexible || isCustomTerm(line);

  return {
    quantity: !isFlatRateService(line.service),
    duration: needsCustomInputs && !isOneTimeService(line.service),
    billingFrequency: needsCustomInputs && !isOneTimeService(line.service),
    price: needsCustomInputs
  };
};

export const getEffectivePrice = (line: DealServiceLine): number =>
  getLineFieldVisibility(line).price
    ? line.price
    : (getSelectedPricing(line)?.price ?? 0);

export const getLineDurationInMonths = (line: DealServiceLine): number => {
  if (isEffectivelyOneTime(line)) return 1;
  if (isFlexibleService(line.service) || isCustomTerm(line)) {
    return getServiceDurationInMonths(line.duration);
  }
  return getServiceDurationInMonths(
    getSelectedPricing(line)?.serviceItemPeriods
  );
};

// Mirrors the add-service drawer: flexible and flat rate cover a whole period.
export const getLineTotal = (line: DealServiceLine): number => {
  if (!hasSelectedTerm(line)) return 0;
  const price = getEffectivePrice(line);
  if (isFlatRateService(line.service)) return price;
  // Mid-typing ("-", "0.") the quantity is NaN — without this the deal total
  // renders as NaN. The line is incomplete until it resolves anyway.
  const quantity = Number.isFinite(line.quantity) ? line.quantity : 0;
  if (isFlexibleService(line.service)) return price * quantity;
  return price * quantity * getLineDurationInMonths(line);
};

export const isLineComplete = (line: DealServiceLine): boolean => {
  if (!hasSelectedTerm(line)) return false;
  const visibility = getLineFieldVisibility(line);
  if (visibility.quantity && !isValidQuantity(line.quantity)) return false;
  if (visibility.duration && line.duration == null) return false;
  if (visibility.billingFrequency && line.billingFrequency == null)
    return false;
  if (visibility.price && !(line.price > 0)) return false;
  return true;
};

export interface DealFinancialEstimate {
  mrr: number;
  arr: number;
  tcv: number;
}

// Local estimate of the backend values, mirroring duplicate/financials.ts.
export const estimateDealFinancials = (
  lines: DealServiceLine[]
): DealFinancialEstimate => {
  let mrr = 0;
  let tcv = 0;

  lines.forEach((line) => {
    if (!isLineComplete(line)) return;

    const total = getLineTotal(line);
    tcv += total;
    if (!isEffectivelyOneTime(line)) {
      const durationInMonths = getLineDurationInMonths(line);
      if (durationInMonths > 0) {
        mrr += total / durationInMonths;
      }
    }
  });

  return { mrr, arr: mrr * 12, tcv };
};

export const buildTermOptions = (
  service: ServiceItem,
  t: TFunction,
  currency: string
): SingleSelectOption[] => {
  if (isFlexibleService(service)) {
    return [
      {
        id: FLEXIBLE_TERM_ID,
        displayName: t("deals.createModal.services.flexibleTerm")
      }
    ];
  }

  const isFlatRate = isFlatRateService(service);

  return service.pricing.map((pricing) => {
    if (pricing.serviceItemPeriods === ServiceDuration.CUSTOM) {
      return {
        id: pricing.id,
        displayName: t("contractTerms.customPricingAndBilling")
      };
    }

    const price = formatCurrency(pricing.price ?? 0, currency);

    if (isOneTimeService(service)) {
      return {
        id: pricing.id,
        displayName: t(
          isFlatRate
            ? "deals.createModal.services.termOneTimeFlatRate"
            : "deals.createModal.services.termOneTimePerUnit",
          { price }
        )
      };
    }

    return {
      id: pricing.id,
      displayName: t(
        isFlatRate
          ? "deals.createModal.services.termRecurringFlatRate"
          : "deals.createModal.services.termRecurringPerUnit",
        {
          duration: getServiceDurationLabel(pricing.serviceItemPeriods, t),
          frequency: getBillingFrequencyLabel(
            pricing.billingFrequency,
            t
          ).toLowerCase(),
          price,
          period: getBillingPeriodLabel(pricing.billingFrequency, t)
        }
      )
    };
  });
};

// "Other" is ServiceDuration.CUSTOM, which needs an end date this form omits.
export const buildDurationOptions = (t: TFunction): SingleSelectOption[] =>
  SERVICE_DURATION_OPTIONS.filter(
    (option) => option.id !== ServiceDuration.CUSTOM
  ).map((option) => ({ id: option.id, displayName: t(option.tKey) }));

// CUSTOM is excluded: it needs an end date this form omits. ONCE is offered
// alongside the recurring cadences per design.
export const buildBillingFrequencyOptions = (
  t: TFunction
): SingleSelectOption[] =>
  BILLING_FREQUENCY_OPTIONS.filter(
    (option) => option.id !== BillingFrequency.CUSTOM
  ).map((option) => ({ id: option.id, displayName: t(option.tKey) }));
